import { NavArrowRight } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { UsageBar } from 'features/usage/components/usage-bar';
import { formatNumber } from 'features/usage/utils/usage-format';
import type { TeamsPerson, TeamsPersonStatus } from '../teams-mock-data';
import { teamsStatusBadgeClassName } from './teams-styles';

function statusBadgeClass(status: TeamsPersonStatus) {
  if (status === 'near_limit') {
    return 'bg-warning-background text-warning-high-emphasis';
  }
  if (status === 'at_daily_limit') {
    return 'bg-error-background text-error-high-emphasis';
  }
  return 'bg-grey-100 text-grey-600';
}

function statusLabel(status: TeamsPersonStatus, t: (key: string, options?: { defaultValue?: string }) => string) {
  if (status === 'near_limit') {
    return t('TEAMS_STATUS_NEAR_LIMIT', { defaultValue: 'Near limit' });
  }
  if (status === 'at_daily_limit') {
    return t('TEAMS_STATUS_AT_DAILY_LIMIT', { defaultValue: 'At daily limit' });
  }
  if (status === 'shared_credits_only') {
    return t('TEAMS_STATUS_SHARED_CREDITS_ONLY', { defaultValue: 'Shared credits only' });
  }
  if (status === 'plan_underused') {
    return t('TEAMS_STATUS_PLAN_UNDERUSED', { defaultValue: 'Plan underused' });
  }
  if (status === 'seat_unused') {
    return t('TEAMS_STATUS_SEAT_UNUSED', { defaultValue: 'Seat unused' });
  }
  return null;
}

type PersonCellProps = {
  person: TeamsPerson;
  nameColor: string;
  mutedColor: string;
};

export function PersonCell({ person, nameColor, mutedColor }: PersonCellProps) {
  const { t } = useTranslation();
  const badge = statusLabel(person.status, t);

  return (
    <div className="flex min-w-0 items-start gap-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-grey-100 text-xs font-medium leading-heading text-grey-800">
        {person.initials}
      </div>
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span title={person.name} className={cn('truncate text-sm font-medium leading-body', nameColor)}>
            {person.name}
          </span>
          {person.isYou && (
            <span className={cn('text-sm leading-body', mutedColor)}>
              {t('TEAMS_YOU', { defaultValue: '(you)' })}
            </span>
          )}
          {badge && (
            <span className={cn(teamsStatusBadgeClassName, statusBadgeClass(person.status))}>
              {badge}
            </span>
          )}
        </div>
        <span title={person.email} className={cn('truncate text-xs leading-heading', mutedColor)}>
          {person.email}
        </span>
      </div>
    </div>
  );
}

type CreditsCellProps = {
  person: TeamsPerson;
  valueColor: string;
  mutedColor: string;
  percentColor: string;
};

export function CreditsCell({ person, valueColor, mutedColor, percentColor }: CreditsCellProps) {
  const { t } = useTranslation();

  if (person.sharedCreditsLabel) {
    return <span className={cn('text-sm leading-body', mutedColor)}>{person.sharedCreditsLabel}</span>;
  }
  if (person.creditsUsed == null || person.creditsLimit == null) {
    return <span className={cn('text-sm leading-body', mutedColor)}>—</span>;
  }

  const percent = (person.creditsUsed / person.creditsLimit) * 100;
  const percentLabel = `${Math.round(percent)}%`;
  const warnPercent = person.status === 'near_limit' || percent >= 80;

  // Fixed-width percent, so every row's bar starts and ends at the same place.
  return (
    <div className="flex w-full max-w-56 flex-col gap-1.5">
      <div className="flex items-baseline gap-1 whitespace-nowrap">
        <span className={cn('text-sm leading-body', valueColor)}>{formatNumber(person.creditsUsed)}</span>
        <span className={cn('text-xs leading-heading', mutedColor)}>
          {t('TEAMS_CREDITS_OF', {
            defaultValue: 'of {{limit}}',
            limit: formatNumber(person.creditsLimit),
          })}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <UsageBar percent={percent} warn={person.status === 'near_limit'} />
        </div>
        <span
          className={cn(
            'w-9 shrink-0 text-right text-xs font-medium leading-none tabular-nums',
            warnPercent ? 'text-warning-high-emphasis' : percentColor
          )}
        >
          {percentLabel}
        </span>
      </div>
    </div>
  );
}

type TeamsUsageCardProps = {
  person: TeamsPerson;
  rank: number;
  onOpen: () => void;
};

/** Phone layout of one person: rank, name and chevron, then Plan and Credits rows and a full-width bar. */
export function TeamsUsageCard({ person, rank, onOpen }: TeamsUsageCardProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const nameColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const mutedColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const valueColor = theme === 'dark' ? 'text-grey-50' : 'text-grey-900';
  const percentColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-800';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';
  const rowClassName = 'flex items-baseline justify-between gap-4 text-sm leading-body';
  const used = person.creditsUsed;
  const limit = person.creditsLimit;
  const hasCredits = used != null && limit != null && !person.sharedCreditsLabel;
  const percent = hasCredits ? (used / limit) * 100 : 0;

  let credits = <span className={mutedColor}>—</span>;
  if (person.sharedCreditsLabel) {
    credits = <span className={mutedColor}>{person.sharedCreditsLabel}</span>;
  } else if (hasCredits) {
    credits = (
      <span className={valueColor}>
        {formatNumber(used)}{' '}
        <span className={cn('text-xs', mutedColor)}>
          {t('TEAMS_CREDITS_OF', { defaultValue: 'of {{limit}}', limit: formatNumber(limit) })}
        </span>
      </span>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'flex w-full flex-col gap-3 rounded-section border bg-true-white p-4 text-left transition-colors hover:bg-grey-50 dark:bg-card dark:hover:bg-sidebar-pill-dark',
          borderColor
        )}
      >
        <div className="flex w-full items-start gap-2">
          {/* Fixed width so the avatar starts at the same place for 1 and 10. */}
          <span className={cn('w-5 shrink-0 pt-2 text-center text-sm tabular-nums', mutedColor)}>{rank}</span>
          <div className="min-w-0 flex-1">
            <PersonCell person={person} nameColor={nameColor} mutedColor={mutedColor} />
          </div>
          <NavArrowRight
            width={16}
            height={16}
            strokeWidth={1.5}
            className={cn('mt-2.5 shrink-0', mutedColor)}
            aria-hidden
          />
        </div>

        <div className={cn('flex w-full flex-col gap-2 border-t pt-3', borderColor)}>
          <div className={rowClassName}>
            <span className={mutedColor}>{t('TEAMS_COL_PLAN', { defaultValue: 'Plan' })}</span>
            <span className={cn('truncate', valueColor)}>{person.plan ?? '—'}</span>
          </div>
          <div className={rowClassName}>
            <span className={mutedColor}>{t('TEAMS_COL_CREDITS', { defaultValue: 'Credits this month' })}</span>
            {credits}
          </div>
          {hasCredits && (
            <div className="flex items-center gap-3 pt-1">
              <div className="min-w-0 flex-1">
                <UsageBar percent={percent} warn={person.status === 'near_limit'} />
              </div>
              <span
                className={cn(
                  'w-9 shrink-0 text-right text-xs font-medium leading-none tabular-nums',
                  person.status === 'near_limit' || percent >= 80 ? 'text-warning-high-emphasis' : percentColor
                )}
              >
                {`${Math.round(percent)}%`}
              </span>
            </div>
          )}
        </div>
      </button>
    </li>
  );
}
